// src/pages/broadcasts/BroadcastsPage.jsx
import { useCallback, useEffect, useMemo, useState } from "react";
import {
  Send,
  Users,
  Link2,
  CheckCircle2,
  AlertCircle,
  Loader2,
  Bell,
} from "lucide-react";
import { notificationService } from "../../services/notificationService";
import { groupService } from "../../services/groupService";
import { useAuthStore } from "../../store/authstore";

const MAX_TITLE = 255;
const MAX_MESSAGE = 1000;

const STATUS = {
  pending: { label: "في الانتظار", color: "var(--text-secondary)" },
  processing: { label: "جاري الإرسال", color: "var(--accent)" },
  done: { label: "تم الإرسال", color: "#10b981" },
  failed: { label: "فشل الإرسال", color: "var(--danger)" },
};

// بيدعم الشكل: [..] أو {results: [..]} أو {data: [..]} أو {data: {results: [..]}}
function toList(payload) {
  if (Array.isArray(payload)) return payload;
  if (Array.isArray(payload?.results)) return payload.results;
  if (Array.isArray(payload?.data)) return payload.data;
  if (Array.isArray(payload?.data?.results)) return payload.data.results;
  return [];
}

function errorText(err) {
  const res = err?.response;
  if (res?.status === 429) return "أرسلت رسائل كثيرة في وقت قصير، انتظر قليلاً ثم حاول مرة أخرى";
  return (
    res?.data?.error_message ||
    res?.data?.detail ||
    "حدثت مشكلة أثناء الإرسال، حاول مرة أخرى"
  );
}

const CSS = `
  .bc-grid { display:grid; grid-template-columns:minmax(0,1.1fr) minmax(0,1fr); gap:22px; align-items:start; }
  @media (max-width:960px){ .bc-grid { grid-template-columns:minmax(0,1fr); } }

  .bc-card {
    background:var(--glass-bg); border:1px solid var(--glass-border);
    border-radius:18px; padding:22px;
    backdrop-filter:blur(20px) saturate(160%); -webkit-backdrop-filter:blur(20px) saturate(160%);
    box-shadow:0 4px 24px rgba(79,124,255,0.07);
  }
  .bc-card h2 { font-size:15px; font-weight:700; color:var(--text-primary); margin-bottom:4px; }
  .bc-sub { font-size:12.5px; color:var(--text-secondary); margin-bottom:18px; line-height:1.7; }

  .bc-field { margin-bottom:16px; }
  .bc-label { display:flex; justify-content:space-between; font-size:12.5px; font-weight:600; color:var(--text-primary); margin-bottom:7px; }
  .bc-count { font-weight:400; color:var(--text-secondary); font-family:'JetBrains Mono',monospace; font-size:11px; }
  .bc-count.over { color:var(--danger); }

  .bc-input, .bc-textarea, .bc-select {
    width:100%; padding:11px 14px; border-radius:12px;
    background:var(--user-btn-bg); border:1px solid var(--glass-border);
    color:var(--text-primary); font-family:'Sora',sans-serif; font-size:13.5px;
    transition:border-color .2s, box-shadow .2s;
  }
  .bc-textarea { min-height:130px; resize:vertical; line-height:1.8; }
  .bc-input:focus-visible, .bc-textarea:focus-visible, .bc-select:focus-visible {
    outline:none; border-color:var(--accent); box-shadow:0 0 0 3px var(--accent-glow);
  }
  .bc-input[dir="ltr"] { text-align:left; }

  .bc-preview {
    display:flex; gap:11px; padding:12px 14px; border-radius:14px; margin-bottom:18px;
    background:var(--chip-bg); border:1px dashed var(--glass-border);
  }
  .bc-preview-icon {
    width:34px; height:34px; border-radius:10px; flex-shrink:0;
    display:flex; align-items:center; justify-content:center; color:#fff;
    background:linear-gradient(135deg,var(--accent),var(--accent-2));
  }
  .bc-preview-title { font-size:13px; font-weight:700; color:var(--text-primary); }
  .bc-preview-body { font-size:12.5px; color:var(--text-secondary); margin-top:2px; line-height:1.6;
    display:-webkit-box; -webkit-line-clamp:3; -webkit-box-orient:vertical; overflow:hidden; word-break:break-word; }

  .bc-send {
    width:100%; display:flex; align-items:center; justify-content:center; gap:9px;
    padding:13px; border:none; border-radius:13px; cursor:pointer;
    font-family:'Sora',sans-serif; font-size:14px; font-weight:600; color:#fff;
    background:linear-gradient(135deg,var(--accent),var(--accent-2));
    box-shadow:0 6px 20px var(--accent-glow); transition:transform .15s, opacity .2s;
  }
  .bc-send:hover:not(:disabled) { transform:translateY(-1px); }
  .bc-send:disabled { opacity:.5; cursor:not-allowed; box-shadow:none; }
  .bc-send:focus-visible { outline:2px solid var(--accent); outline-offset:3px; }

  .bc-alert { display:flex; gap:9px; align-items:flex-start; padding:11px 14px; border-radius:12px; font-size:13px; line-height:1.7; margin-bottom:16px; }
  .bc-alert.ok  { background:rgba(16,185,129,0.10); color:#059669; }
  .bc-alert.err { background:var(--danger-bg); color:var(--danger); }
  .bc-alert svg { flex-shrink:0; margin-top:3px; }

  .bc-audience {
    display:flex; align-items:center; gap:9px; padding:10px 14px; margin-bottom:18px;
    border-radius:12px; background:var(--chip-bg); border:1px solid var(--glass-border);
    font-size:13px; color:var(--text-primary);
  }

  .bc-list { list-style:none; display:flex; flex-direction:column; gap:12px; max-height:640px; overflow-y:auto; }
  .bc-item { padding:14px 16px; border-radius:14px; background:var(--chip-bg); border:1px solid var(--glass-border); }
  .bc-item-head { display:flex; justify-content:space-between; gap:10px; align-items:center; margin-bottom:6px; }
  .bc-item-title { font-size:13.5px; font-weight:700; color:var(--text-primary); }
  .bc-item-msg { font-size:13px; color:var(--text-secondary); line-height:1.7; white-space:pre-wrap; word-break:break-word; }
  .bc-item-meta { display:flex; flex-wrap:wrap; gap:6px 14px; margin-top:10px; font-size:11.5px; color:var(--text-secondary); }
  .bc-status { display:inline-flex; align-items:center; gap:6px; font-size:12px; font-weight:600; white-space:nowrap; }
  .bc-dot { width:7px; height:7px; border-radius:50%; background:currentColor; }
  .bc-empty { text-align:center; padding:36px 10px; color:var(--text-secondary); font-size:13px; line-height:1.9; }
  .bc-spin { animation:bcspin 1s linear infinite; }
  @keyframes bcspin { to { transform:rotate(360deg); } }
  @media (prefers-reduced-motion:reduce){ .bc-spin{animation:none;} .bc-send{transition:none;} }
`;

export default function BroadcastsPage() {
  const { user } = useAuthStore();
  const isAdmin = user?.user_type === "admin";

  const [groups, setGroups] = useState([]);
  const [groupsLoading, setGroupsLoading] = useState(false);
  const [groupId, setGroupId] = useState("");

  const [form, setForm] = useState({ title: "", message: "", link: "" });
  const [history, setHistory] = useState([]);
  const [historyLoading, setHistoryLoading] = useState(true);
  const [sending, setSending] = useState(false);
  const [feedback, setFeedback] = useState(null); // {type: 'ok' | 'err', text}

  const set = (key) => (e) => setForm((f) => ({ ...f, [key]: e.target.value }));

  // الأدمن → كل الطلاب | المدرس → لازم يختار مجموعة
  const canTarget = isAdmin || !!groupId;

  const groupName = useMemo(
    () => groups.find((g) => String(g.id) === String(groupId))?.name ?? "",
    [groups, groupId]
  );

  // ── مجموعات المدرس ──
  useEffect(() => {
    if (!user || isAdmin) return;
    let cancelled = false;
    setGroupsLoading(true);
    Promise.resolve()
      .then(() => groupService.listGroups())
      .then((data) => {
        if (cancelled) return;
        const list = toList(data);
        setGroups(list);
        if (list.length) setGroupId((cur) => cur || String(list[0].id));
      })
      .catch(() => !cancelled && setGroups([]))
      .finally(() => !cancelled && setGroupsLoading(false));
    return () => {
      cancelled = true;
    };
  }, [user, isAdmin]);

  // ── سجل الإرسال ──
  const loadHistory = useCallback(async () => {
    if (!canTarget) {
      setHistory([]);
      setHistoryLoading(false);
      return;
    }
    try {
      const data = isAdmin
        ? await notificationService.adminBroadcasts()
        : await notificationService.groupBroadcasts(groupId);
      setHistory(Array.isArray(data?.data) ? data.data : []);
    } catch {
      setHistory([]);
    } finally {
      setHistoryLoading(false);
    }
  }, [canTarget, isAdmin, groupId]);

  useEffect(() => {
    setHistoryLoading(true);
    setFeedback(null);
    loadHistory();
  }, [loadHistory]);

  // طول ما في رسالة لسه بتتبعت، حدّث الحالة كل 3 ثواني
  const hasActive = history.some(
    (b) => b.status === "pending" || b.status === "processing"
  );
  useEffect(() => {
    if (!hasActive) return;
    const t = setInterval(loadHistory, 3000);
    return () => clearInterval(t);
  }, [hasActive, loadHistory]);

  // ── الإرسال ──
  const link = form.link.trim();
  const message = form.message.trim();
  const linkInvalid = link !== "" && !/^https?:\/\//i.test(link);
  const canSend =
    !sending &&
    canTarget &&
    message.length > 0 &&
    message.length <= MAX_MESSAGE &&
    form.title.length <= MAX_TITLE &&
    !linkInvalid;

  const handleSend = async (e) => {
    e.preventDefault();
    if (!canSend) return;

    const audience = isAdmin ? "كل الطلاب" : `طلاب مجموعة ${groupName}`;
    if (!window.confirm(`سيتم إرسال الإشعار إلى ${audience}. هل تؤكد الإرسال؟`)) return;

    const payload = { message };
    if (form.title.trim()) payload.title = form.title.trim();
    if (link) payload.link = link;

    setSending(true);
    setFeedback(null);
    try {
      const data = isAdmin
        ? await notificationService.sendAdminBroadcast(payload)
        : await notificationService.sendGroupBroadcast(groupId, payload);
      setFeedback({ type: "ok", text: data?.message || "جاري إرسال الإشعار" });
      setForm({ title: "", message: "", link: "" });
      if (data?.data) setHistory((prev) => [data.data, ...prev].slice(0, 50));
    } catch (err) {
      setFeedback({ type: "err", text: errorText(err) });
    } finally {
      setSending(false);
    }
  };

  const previewTitle =
    form.title.trim() || (isAdmin ? "Sabr" : `${user?.full_name ?? ""} · ${groupName}`);

  const noGroups = !isAdmin && !groupsLoading && groups.length === 0;

  return (
    <div dir="rtl">
      <style>{CSS}</style>

      <div className="bc-grid">
        {/* ───────── إرسال إشعار ───────── */}
        <form className="bc-card" onSubmit={handleSend}>
          <h2>إرسال إشعار جديد</h2>
          <p className="bc-sub">
            {isAdmin
              ? "سيصل الإشعار إلى جميع الطلاب على الهاتف وسيظهر في شاشة الإشعارات."
              : "سيصل الإشعار إلى طلاب المجموعة التي تختارها على الهاتف وسيظهر في شاشة الإشعارات."}
          </p>

          {isAdmin ? (
            <div className="bc-audience">
              <Users size={16} color="var(--accent)" />
              <span>المستلمون: كل الطلاب</span>
            </div>
          ) : (
            <div className="bc-field">
              <label className="bc-label" htmlFor="bc-group">
                المجموعة
              </label>
              <select
                id="bc-group"
                className="bc-select"
                value={groupId}
                onChange={(e) => setGroupId(e.target.value)}
                disabled={groupsLoading || groups.length === 0}
              >
                {groupsLoading && <option>جاري التحميل...</option>}
                {noGroups && <option>لا توجد مجموعات</option>}
                {groups.map((g) => (
                  <option key={g.id} value={g.id}>
                    {g.name}
                  </option>
                ))}
              </select>
            </div>
          )}

          <div className="bc-field">
            <label className="bc-label" htmlFor="bc-title">
              <span>العنوان (اختياري)</span>
              <span className={`bc-count${form.title.length > MAX_TITLE ? " over" : ""}`}>
                {form.title.length}/{MAX_TITLE}
              </span>
            </label>
            <input
              id="bc-title"
              className="bc-input"
              value={form.title}
              onChange={set("title")}
              placeholder={isAdmin ? "الافتراضي: Sabr" : "الافتراضي: اسمك · اسم المجموعة"}
            />
          </div>

          <div className="bc-field">
            <label className="bc-label" htmlFor="bc-message">
              <span>نص الرسالة</span>
              <span className={`bc-count${form.message.length > MAX_MESSAGE ? " over" : ""}`}>
                {form.message.length}/{MAX_MESSAGE}
              </span>
            </label>
            <textarea
              id="bc-message"
              className="bc-textarea"
              value={form.message}
              onChange={set("message")}
              placeholder="اكتب الرسالة التي تريد إيصالها إلى الطلاب"
            />
          </div>

          <div className="bc-field">
            <label className="bc-label" htmlFor="bc-link">
              رابط (اختياري)
            </label>
            <input
              id="bc-link"
              className="bc-input"
              dir="ltr"
              inputMode="url"
              value={form.link}
              onChange={set("link")}
              placeholder="https://"
              aria-invalid={linkInvalid}
            />
            {linkInvalid && (
              <div style={{ color: "var(--danger)", fontSize: 12, marginTop: 6 }}>
                يجب أن يبدأ الرابط بـ http:// أو https://
              </div>
            )}
          </div>

          {message && (
            <div className="bc-preview" aria-label="معاينة الإشعار">
              <div className="bc-preview-icon">
                <Bell size={16} />
              </div>
              <div style={{ minWidth: 0 }}>
                <div className="bc-preview-title">{previewTitle}</div>
                <div className="bc-preview-body">{message}</div>
              </div>
            </div>
          )}

          {feedback && (
            <div className={`bc-alert ${feedback.type}`} role="status">
              {feedback.type === "ok" ? <CheckCircle2 size={16} /> : <AlertCircle size={16} />}
              <span>{feedback.text}</span>
            </div>
          )}

          <button type="submit" className="bc-send" disabled={!canSend}>
            {sending ? <Loader2 size={17} className="bc-spin" /> : <Send size={17} />}
            {sending ? "جاري الإرسال..." : "إرسال الإشعار"}
          </button>
        </form>

        {/* ───────── السجل ───────── */}
        <section className="bc-card" aria-live="polite">
          <h2>آخر الإشعارات المُرسلة</h2>
          <p className="bc-sub">
            {isAdmin
              ? "آخر 50 إشعاراً أُرسلت إلى جميع الطلاب."
              : groupName
              ? `آخر 50 رسالة أُرسلت إلى مجموعة ${groupName}.`
              : "اختر مجموعة لتشاهد رسائلها."}
          </p>

          {historyLoading ? (
            <div className="bc-empty">
              <Loader2 size={20} className="bc-spin" />
            </div>
          ) : history.length === 0 ? (
            <div className="bc-empty">
              {noGroups
                ? "لا توجد مجموعات متاحة لك حتى الآن، يجب أن تكون لديك مجموعة تضم طلاباً لتتمكن من الإرسال."
                : "لم يتم إرسال أي إشعار بعد. ستظهر أول رسالة هنا."}
            </div>
          ) : (
            <ul className="bc-list">
              {history.map((b) => {
                const st = STATUS[b.status] ?? STATUS.pending;
                return (
                  <li key={b.id} className="bc-item">
                    <div className="bc-item-head">
                      <span className="bc-item-title">{b.title}</span>
                      <span className="bc-status" style={{ color: st.color }}>
                        {b.status === "processing" ? (
                          <Loader2 size={12} className="bc-spin" />
                        ) : (
                          <span className="bc-dot" />
                        )}
                        {st.label}
                      </span>
                    </div>
                    <div className="bc-item-msg">{b.message}</div>
                    <div className="bc-item-meta">
                      <span>
                        {new Date(b.created_at).toLocaleString("ar-EG", {
                          dateStyle: "medium",
                          timeStyle: "short",
                        })}
                      </span>
                      {b.created_by_name && <span>بواسطة {b.created_by_name}</span>}
                      {b.status === "done" && <span>وصل لـ {b.recipients_count} طالب</span>}
                      {b.status === "failed" && b.recipients_count > 0 && (
                        <span>وصل الى {b.recipients_count} طالب قبل أن يفشل</span>
                      )}
                      {b.link && (
                        <a
                          href={b.link}
                          target="_blank"
                          rel="noreferrer"
                          style={{
                            color: "var(--accent)",
                            display: "inline-flex",
                            alignItems: "center",
                            gap: 4,
                          }}
                        >
                          <Link2 size={12} /> الرابط
                        </a>
                      )}
                    </div>
                  </li>
                );
              })}
            </ul>
          )}
        </section>
      </div>
    </div>
  );
}